// Map context — dedicated to the Mapbox GL instance (US-874 · R-5).
//
// Holds ONLY the top-level `mapRef` and the map lifecycle flag. Layer/source
// logic (US-875) and UI/selection state (see ./UIContext) deliberately live
// elsewhere: keeping this context map-only avoids the legacy "god context" and
// the broad re-renders it caused. Consume it through the `useMap` hook.
import { createContext, useCallback, useMemo, useRef, useState } from "react";

export const MapContext = createContext(null);

export const MapProvider = ({ children }) => {
  // Top-level ref to the Mapbox GL instance; the map mounts into this in US-875.
  const mapRef = useRef(null);
  // Lifecycle flag flipped once the map's `load` event fires (wired in US-875).
  const [isMapReady, setIsMapReady] = useState(false);

  // Stable lifecycle callbacks so consumers keep a steady value identity.
  const markMapReady = useCallback(() => setIsMapReady(true), []);
  const resetMap = useCallback(() => setIsMapReady(false), []);

  const value = useMemo(
    () => ({ mapRef, isMapReady, markMapReady, resetMap }),
    [isMapReady, markMapReady, resetMap]
  );

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};
